'use client';

/**
 * **An "other" asset's instruction, on the record** (VALE-1940).
 *
 * The instruction was edited only on the Redemption tab, so the AI review showed
 * the value it proposed with nothing to correct it by, and that value decides
 * the item's Redemption bucket and whether its realised cash counts as
 * distributable. This mounts that tab's own editor, `OtherInstructionSelector`
 * and `OtherBeneficiaryPanel`, rather than a second one, and hands every answer
 * to the host as one `SetOtherInstructionRequest`: the host decides whether that
 * goes through the instruction endpoint (a saved item, where the status moves
 * with it) or into the buffered record (the add dialog, the AI review).
 *
 * **A transfer commits only once someone receives it.** The endpoint refuses a
 * transfer with no beneficiary, so choosing it opens the picker and waits; each
 * tick after the first commits the selection. The first choice of a transfer is
 * pre-filled from the will where a gift names this item (VALE-1431), exactly as
 * the Redemption sheet does.
 *
 * `allowBeneficiaries` is false where the recipients cannot be saved: the AI
 * apply keeps an "other" asset's `transferBeneficiaries` off the record (the
 * field registry marks them solicitor-recorded), so offering the picker there
 * would show a choice that is not written.
 */

import { useState } from 'react';
import { useSetOtherInstruction } from '@/lib/hooks/use-other-workflows';
import { toast } from '@/lib/toast';
import type { Contact } from '@/lib/services/contacts/types';
import type {
  OtherInstruction,
  SetOtherInstructionRequest
} from '@/lib/services/redemption';
import type { ItemBeneficiaryShare } from '@/lib/services/entitlements/specific-gift-defaults';
import {
  buildContactBeneficiarySeed,
  namedBeneficiaryContacts
} from '@/lib/services/entitlements/contact-beneficiary-seed';
import { OtherInstructionSelector } from '@/components/redemption/other-instructions/instruction-selector';
import {
  OtherBeneficiaryPanel,
  type OtherTransferBeneficiary
} from '@/components/redemption/other-instructions/beneficiary-panel';

export interface OtherInstructionKeptProps {
  instruction: OtherInstruction | null;
  /** The contact ids the item already transfers to. */
  beneficiaryIds: number[];
  contacts: Contact[];
  entitlementBeneficiaryShares?: ItemBeneficiaryShare[];
  allowBeneficiaries: boolean;
  onChange: (request: SetOtherInstructionRequest) => void;
}

export function OtherInstructionKept({
  instruction,
  beneficiaryIds,
  contacts,
  entitlementBeneficiaryShares,
  allowBeneficiaries,
  onChange
}: OtherInstructionKeptProps) {
  const people = namedBeneficiaryContacts(contacts);
  const named = (ids: number[]): OtherTransferBeneficiary[] =>
    ids.flatMap((id) => {
      const person = people.find((p) => p.id === id);
      return person
        ? [
            {
              contactId: id,
              name: `${person.firstName} ${person.lastName}`.trim()
            }
          ]
        : [];
    });

  /*
    A transfer chosen here but not yet given a recipient has not been committed,
    so the record still holds the previous instruction. Local until it is.
  */
  const [pendingTransfer, setPendingTransfer] = useState(false);
  const [selected, setSelected] = useState<OtherTransferBeneficiary[]>(() =>
    named(beneficiaryIds)
  );
  const shown = pendingTransfer ? 'transfer-to-beneficiary' : instruction;

  const commitTransfer = (next: OtherTransferBeneficiary[]) =>
    onChange({
      instruction: 'transfer-to-beneficiary',
      transferBeneficiaries: next.map((b) => ({ contactId: b.contactId }))
    });

  const handleSelect = (next: OtherInstruction) => {
    if (next !== 'transfer-to-beneficiary') {
      setPendingTransfer(false);
      setSelected([]);
      onChange({ instruction: next });
      return;
    }
    if (!allowBeneficiaries) {
      onChange({ instruction: next });
      return;
    }
    const seed =
      selected.length === 0
        ? buildContactBeneficiarySeed(entitlementBeneficiaryShares, people)
        : null;
    const start = seed ?? selected;
    setSelected(start);
    if (start.length > 0) {
      setPendingTransfer(false);
      commitTransfer(start);
    } else {
      setPendingTransfer(true);
    }
  };

  const handleToggle = (contact: {
    id: number;
    firstName: string;
    lastName: string;
  }) => {
    const next = selected.some((s) => s.contactId === contact.id)
      ? selected.filter((s) => s.contactId !== contact.id)
      : [
          ...selected,
          {
            contactId: contact.id,
            name: `${contact.firstName} ${contact.lastName}`.trim()
          }
        ];
    setSelected(next);
    if (next.length === 0) {
      setPendingTransfer(true);
      return;
    }
    setPendingTransfer(false);
    commitTransfer(next);
  };

  return (
    <div className="space-y-4">
      <OtherInstructionSelector selected={shown} onSelect={handleSelect} />
      {allowBeneficiaries && shown === 'transfer-to-beneficiary' && (
        <OtherBeneficiaryPanel
          contacts={people}
          selected={selected}
          onToggle={handleToggle}
        />
      )}
    </div>
  );
}

/**
 * **The panel on a saved record, answering through the instruction endpoint**
 * (VALE-1940): the one the Redemption tab saves through, so the item's status
 * and realised cash move with the answer. Its own component so the mutation
 * hook lives only where a saved other asset is on screen.
 */
export function SavedOtherInstructionKept({
  matterId,
  itemId,
  ...panel
}: Omit<OtherInstructionKeptProps, 'allowBeneficiaries' | 'onChange'> & {
  matterId: number;
  itemId: number;
}) {
  const setOtherInstruction = useSetOtherInstruction(matterId);
  return (
    <OtherInstructionKept
      {...panel}
      allowBeneficiaries
      onChange={(request) => {
        setOtherInstruction
          .mutateAsync({ itemId, data: request })
          .catch(() => toast.error('Failed to save instructions'));
      }}
    />
  );
}
